import React, { useEffect, useRef, useState } from 'react';
import BottomSheet from './BottomSheet';
import { titleCaseSongName } from '../hooks/songDisplayName';
import useSongMetadataSearch from '../hooks/useSongMetadataSearch';
import { deriveRecordingCategory, CATEGORY } from '../hooks/songSearchRank';
import './AddSongSheet.css';

/** Cast/soundtrack/workshop wording, from either the derived `recordingType`
 *  or the album title itself. Both `inferRecordingType` implementations
 *  (AppleMusicService, MusicService) fall back to the literal 'Album' for
 *  everything else, which is true of most rows and tells a teacher nothing. */
const AUTHORITY_RE = /cast|soundtrack|workshop|demo/i;

/**
 * "Idina Menzel · Wicked (Original Broadway Cast Recording) · 2003 · Schwartz · showtune"
 *
 * Composed defensively — every field is optional across the three source APIs.
 *
 * ORDER IS LOAD-BEARING, not cosmetic. This renders into a clamped line (see
 * .ld-addsong__resultmeta), so anything at the tail is invisible on exactly the
 * long rows where the teacher is struggling to tell versions apart. The
 * sequence is therefore "what identifies this RECORDING" first, then the
 * song-level facts:
 *
 *   1. show name (musicals only)
 *   2. artist       <- the single highest-signal field. A row by "Twinkle
 *                      Twinkle Little Rock Star" is rejected in under a second
 *                      once the performer is visible, and was indistinguishable
 *                      from an original cast recording before it was.
 *   3. album / recording type   <- where authority usually shows
 *   4. year
 *   5. composers / lyricists    <- deliberately AFTER the version identifiers:
 *                      credits are IDENTICAL across every cover of a song, so
 *                      they cannot disambiguate one row from another. They are
 *                      here because they confirm the right SONG, not the right
 *                      recording.
 *   6. genre
 *
 * The title is deliberately NOT repeated — it is already rendered as the row
 * title directly above, and on a clamped line those characters cost more than
 * they carry. Only a musical's SHOW name (different information) leads.
 */
export const buildMeta = (m, renderedTitle = '') => {
	const parts = [];
	const showName = m.isMusical ? m.musicalName : '';
	if (showName && showName !== renderedTitle) parts.push(showName);

	if (m.artist) parts.push(m.artist);

	// Prefer the album title over the derived type: "Wicked (Original Broadway
	// Cast Recording)" says everything 'Cast Recording' does and names the
	// production too. Suppress it when it merely echoes something already shown.
	const album =
		m.album && m.album !== renderedTitle && m.album !== showName ? m.album : '';
	if (album) parts.push(album);

	// Label the recording from the SAME derivation the ranking uses, so the row
	// a teacher sees at the top cannot disagree with why it was put there. This
	// also reaches Discogs rows, whose stored `recordingType` is a format string
	// ("LP Album") and never says "cast" even when the record is one.
	//
	// Skipped when the album title has already said it, or the row would read
	// "…(Original Broadway Cast Recording) · Cast Recording".
	const category = deriveRecordingCategory(m);
	if (category !== CATEGORY.OTHER && !AUTHORITY_RE.test(album)) {
		parts.push(category === CATEGORY.CAST ? 'Cast Recording' : 'Soundtrack');
	}

	const year = m.releaseYear || m.yearWritten;
	if (year) parts.push(String(year));

	const credits = [...(m.composers || []), ...(m.lyricists || [])].filter(Boolean);
	if (credits.length) parts.push(credits.slice(0, 2).join(' / '));

	// Apple's genre for a film song is the literal "Soundtrack", which on a row
	// whose album is already "…(Original TV Movie Soundtrack)" prints the word
	// twice. Suppress only that echo — narrowly, on the authority wording, NOT
	// on any substring match, because genre "Pop" against an album called
	// "Popular Songs" would be a false positive.
	const genre = m.isMusical ? 'showtune' : (m.genres || [])[0];
	const genreEchoesAlbum =
		genre && AUTHORITY_RE.test(genre) && AUTHORITY_RE.test(album);
	if (genre && !genreEchoesAlbum) parts.push(genre);

	return parts.join(' · ');
};

/**
 * Add a song — search-first, never a gate (design handoff §6). The metadata
 * match fills the song header (show, composer, year, key) and powers the
 * lyrics fetch; the manual row is always present and always works, because a
 * teacher who can't find the exact match still needs to jot on the song NOW.
 */
/**
 * Row identity for the preview player. The route IS the identity elsewhere in
 * this codebase for the same reason (dashboardCards): a label gets reworded.
 * Falls back to the index so two credit-free rows from the same source cannot
 * collide and play each other.
 */
const rowKey = (m, i) => m.recordingId || m.musicbrainzId || `row-${i}`;

/**
 * Should we suggest adding the show name?
 *
 * THE CASE THIS EXISTS FOR (Anthony, 2026-08-03). Searching "Don't Stop
 * Believin'" returns nine Journey entries, two Glee, and the Rock of Ages MOVIE
 * soundtrack. The Broadway cast recording is not in the top 12 from ANY source,
 * so the ranking correctly surfaced the soundtrack — it never had a cast
 * recording to rank. Adding the show ("Don't Stop Believin' Rock of Ages")
 * returns the Original Broadway Cast Recording as the FIRST Apple result.
 *
 * So this is a RECALL problem, not a ranking one, and no amount of scoring
 * fixes a row that is absent from the response. The only thing that helps is
 * the teacher typing the show — and nothing in the UI said that was possible.
 * Jukebox musicals make this routine: the pop original is far more famous than
 * the cast recording, so the bare title will essentially never surface the
 * stage version.
 *
 * Fires on NO CAST RECORDING, even when a soundtrack is present — that is
 * precisely the Rock of Ages case, where a movie soundtrack came back and the
 * stage version a musical-theatre studio wants did not.
 *
 * Deliberately silent when there are no results at all: the "No matches" line
 * and the manual-add row already own that moment, and stacking a second hint
 * on top would bury them.
 */
export const shouldSuggestShow = (options = []) =>
	options.length > 0 &&
	!options.some((o) => deriveRecordingCategory(o) === CATEGORY.CAST);

const AddSongSheet = ({ onAdd, studentId = null, onClose, optionsOverride = null }) => {
	const [query, setQuery] = useState('');
	const [busy, setBusy] = useState(false);
	const [searched, setSearched] = useState(false);
	// Which row is previewing, by rowKey. ONE audio element for the whole sheet,
	// so starting a second preview necessarily stops the first — two 30-second
	// clips playing over each other mid-lesson is the obvious failure here.
	const [playingKey, setPlayingKey] = useState(null);
	const audioRef = useRef(null);
	const {
		search,
		resolveOption,
		options: searchedOptions,
		isSearching,
		searchError,
		reset,
	} = useSongMetadataSearch({ studentId });

	// Render-only escape hatch for measuring the result rows without a live
	// three-API search (this sheet sits behind an admin route, so the rows
	// cannot otherwise be put on screen). Mirrors WorkTasksModule's
	// `tasksOverride` and the invite panel's `invitesOverride`. Never passed in
	// production — the real caller supplies onAdd/studentId only.
	const options = optionsOverride || searchedOptions;

	// ONE ADD PER SHEET, LOCKED ON THE FIRST TAP (2026-10-22). Picking a result
	// first resolves its metadata over the network (a second or two) and only
	// then reached addAndClose, so taps during that wait each started their
	// own add, and `busy` could not stop them: every tap's handler read the
	// `false` from the render it came from. Addison Swenson got three
	// "Placido zeffiretto" rows 50 ms apart that way. A ref is read and set in
	// the same tick, so the second tap sees the first.
	const inFlight = useRef(false);
	const [addingKey, setAddingKey] = useState(null);

	const claim = (key = null) => {
		if (inFlight.current) return false;
		inFlight.current = true;
		setBusy(true);
		setAddingKey(key);
		return true;
	};

	const release = () => {
		inFlight.current = false;
		setBusy(false);
		setAddingKey(null);
	};

	const addAndClose = async (name, metadata) => {
		if (!name) {
			release();
			return;
		}
		try {
			await onAdd(name, metadata || null);
			onClose();
		} catch (err) {
			console.error('Error adding song:', err);
			release();
		}
	};

	const addManually = () => {
		if (!query.trim() || !claim('manual')) return;
		addAndClose(titleCaseSongName(query), null);
	};

	const runSearch = async () => {
		const trimmed = query.trim();
		if (!trimmed || isSearching) return;
		setSearched(true);
		const result = await search(trimmed);
		if (result.kind === 'resolved' && claim('search')) {
			await addAndClose(result.name, result.metadata);
		}
		// 'options' → options[] now populated, rows render below.
		// 'none' → falls through to the always-present manual row.
	};

	const pickOption = async (metadata, key) => {
		if (!claim(key)) return;
		try {
			const resolved = await resolveOption(metadata);
			await addAndClose(resolved.name, resolved.metadata);
		} catch (err) {
			console.error('Error adding song:', err);
			release();
		}
	};

	/**
	 * Start, stop, or switch the 30-second preview.
	 *
	 * `play()` returns a promise that REJECTS on autoplay policy or a dead
	 * media URL, and an unhandled rejection here would surface as a console
	 * error with the button stuck in the "playing" state. Caught, and the state
	 * rolled back, so the control always reflects what is actually audible.
	 */
	const togglePreview = (key, url) => {
		const audio = audioRef.current;
		if (!audio || !url) return;
		if (playingKey === key) {
			audio.pause();
			setPlayingKey(null);
			return;
		}
		audio.pause();
		audio.src = url;
		setPlayingKey(key);
		const started = audio.play();
		if (started && typeof started.catch === 'function') {
			started.catch(() => setPlayingKey(null));
		}
	};

	// Stop the clip when the sheet goes away — a preview outliving the sheet
	// that started it would keep playing over the lesson with no way to stop it.
	useEffect(() => {
		const audio = audioRef.current;
		return () => {
			if (audio) {
				audio.pause();
				audio.src = '';
			}
		};
	}, []);

	const onQueryChange = (e) => {
		setQuery(e.target.value);
		if (searched) {
			setSearched(false);
			reset();
		}
	};

	return (
		<BottomSheet title="Add a song" titleId="ld-addsong-title" onClose={onClose} className="ld-addsong">
			<div className="ld-addsong__body">
				<div className="ld-addsong__searchfield" data-guide="addsong-search">
					<i className="fa-sharp fa-solid fa-magnifying-glass" aria-hidden="true" />
					<input
						className="ld-addsong__field"
						type="text"
						autoFocus
						value={query}
						placeholder="Song title"
						enterKeyHint="search"
						onChange={onQueryChange}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								e.preventDefault();
								runSearch();
							}
						}}
					/>
				</div>

				{isSearching && <p className="ld-addsong__status">Searching…</p>}
				{searchError && <p className="ld-addsong__status ld-addsong__status--error">{searchError}</p>}
				{searched && !isSearching && !searchError && options.length === 0 && (
					<p className="ld-addsong__status">No matches — add it manually below.</p>
				)}

				{shouldSuggestShow(options) && (
					<p className="ld-addsong__hint">
						<i className="fa-sharp fa-solid fa-masks-theater" aria-hidden="true" />
						<span>
							Looking for a stage version? Add the show name — try{' '}
							<strong>{`${query.trim() || 'song title'} <show>`}</strong>.
						</span>
					</p>
				)}

				{options.length > 0 && (
					<ul className="ld-addsong__results">
						{options.map((metadata, i) => {
							const key = rowKey(metadata, i);
							const isPlaying = playingKey === key;
							return (
								<li key={key}>
									<div className="ld-addsong__result">
										{/* The thumbnail slot is ALWAYS rendered, even with no
										    artwork. Apple and Discogs carry cover images and
										    MusicBrainz does not, and since the ranking floats
										    cast recordings to the top, the first row is often a
										    source with no image — a slot that appears and
										    disappears would make the list ragged exactly where
										    it is being scanned fastest. Fetching MusicBrainz art
										    from the Cover Art Archive would cost a request per
										    row, which is what the perf pass just removed. */}
										{metadata.artworkUrl ? (
											<img
												className="ld-addsong__resultart"
												src={metadata.artworkUrl}
												alt=""
												loading="lazy"
												aria-hidden="true"
											/>
										) : (
											<span
												className="ld-addsong__resultart ld-addsong__resultart--empty"
												aria-hidden="true">
												<i className="fa-sharp fa-solid fa-music" />
											</span>
										)}

										<div className="ld-addsong__resulttext">
											<span className="ld-addsong__resulttitle">{metadata.title}</span>
											<span className="ld-addsong__resultmeta">
												{buildMeta(metadata, metadata.title)}
											</span>
										</div>

										{/* Apple-only: iTunes is the sole source carrying a clip.
										    Absent rather than disabled — a disabled play button
										    implies a preview exists and is unavailable, when in
										    fact there is nothing to play. */}
										{metadata.previewUrl && (
											<button
												type="button"
												className={`ld-addsong__resultpreview${
													isPlaying ? ' is-playing' : ''
												}`}
												onClick={() => togglePreview(key, metadata.previewUrl)}
												aria-pressed={isPlaying}
												title={isPlaying ? 'Stop preview' : 'Play a 30-second preview'}
												aria-label={
													isPlaying
														? `Stop preview of ${metadata.title}`
														: `Play a 30-second preview of ${metadata.title}`
												}>
												<i
													className={`fa-sharp fa-solid ${
														isPlaying ? 'fa-stop' : 'fa-play'
													}`}
													aria-hidden="true"
												/>
											</button>
										)}

										<button
											type="button"
											className="ld-addsong__resultadd"
											data-guide="addsong-add"
											disabled={busy}
											aria-busy={addingKey === key}
											onClick={() => pickOption(metadata, key)}>
											{addingKey === key ? 'Adding…' : 'Add'}
										</button>
									</div>
								</li>
							);
						})}
					</ul>
				)}

				{/* One player for the sheet. Rendered unconditionally so the ref is
				    always available; `src` is set on demand by togglePreview. */}
				<audio
					ref={audioRef}
					preload="none"
					onEnded={() => setPlayingKey(null)}
					onError={() => setPlayingKey(null)}
				/>

				{/* A manual add has no resolved title to adopt, so casing is the only
				    tidy available — and the LABEL shows the tidied name rather than
				    the raw typing, so the button never promises one name and save
				    another. */}
				<button
					type="button"
					className="ld-addsong__manual"
					data-guide="addsong-manual"
					disabled={!query.trim() || busy}
					onClick={addManually}>
					<i className="fa-sharp fa-solid fa-pen" aria-hidden="true" />
					{query.trim()
						? `Can't find it? Add "${titleCaseSongName(query)}" manually`
						: "Can't find it? Add it manually"}
				</button>
			</div>
		</BottomSheet>
	);
};

export default AddSongSheet;
